import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, X, Save, Loader2, FileText, ImageOff } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { adminApi, absoluteMediaUrl, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';
import { MediaPicker } from './MediaPicker';

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY || '';

/**
 * Generic bilingual CRUD screen for content collections
 * (certificates, services, projects, partners, gallery, client-logos).
 * Pass `collection` + `titleKey`.
 *
 * `categoryOptions` (optional): array of { value, ar, en } — when provided the
 * drawer shows a category dropdown. Used by partners.
 * `showMetadata` (optional): when true, the drawer exposes a JSON textarea to
 * edit the free-form `metadata` object.
 * `projectFields` (optional): dedicated UI for projects — lat/lng + map picker,
 * bilingual client/region — all still saved into the `metadata` object.
 */
const AdminContent = ({ collection, titleKey, showPdf = false, categoryOptions = null, showMetadata = false, projectFields = false }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited or 'new'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(null); // { field: 'image' | 'pdf', accept }
  const [metadataText, setMetadataText] = useState('{}');
  const [metadataErr, setMetadataErr] = useState('');

  const blank = {
    title_ar: '', title_en: '', description_ar: '', description_en: '',
    image: '', pdf: '', order: 0, published: true,
    category: '', metadata: {},
  };

  const openEditor = (item) => {
    const clone = { ...item };
    clone.category = clone.category || '';
    clone.metadata = clone.metadata || {};
    setEditing(clone);
    setMetadataText(JSON.stringify(clone.metadata, null, 2));
    setMetadataErr('');
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/content/${collection}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const save = async () => {
    setSaving(true); setError('');
    // Parse metadata JSON — safe even if the drawer doesn't expose the field.
    let parsedMeta = editing.metadata || {};
    if (showMetadata) {
      try {
        parsedMeta = JSON.parse(metadataText || '{}');
        if (typeof parsedMeta !== 'object' || Array.isArray(parsedMeta)) {
          throw new Error('metadata must be an object');
        }
        setMetadataErr('');
      } catch (err) {
        setMetadataErr(String(err.message || err));
        setSaving(false);
        return;
      }
    }
    try {
      const payload = { ...editing, metadata: parsedMeta };
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.collection;
      if (editing.id) {
        await adminApi.put(`/content/${collection}/${editing.id}`, payload);
      } else {
        await adminApi.post(`/content/${collection}`, payload);
      }
      setEditing(null);
      await fetchItems();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await adminApi.delete(`/content/${collection}/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
  };

  const txt = isAr ? 'text-right' : 'text-left';

  return (
    <div className="space-y-6">
      <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex-1">{t[titleKey]}</h1>
        <button
          type="button"
          data-testid={`admin-${collection}-new`}
          onClick={() => openEditor(blank)}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow ${isAr ? 'flex-row-reverse' : ''}`}
        >
          <Plus className="w-4 h-4" /> {isAr ? 'إضافة' : 'Add new'}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-14">{t.noData}</p>
      ) : (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className={`px-4 py-3 w-16 ${txt}`}>#</th>
                  <th className={`px-4 py-3 w-20 ${txt}`}></th>
                  <th className={`px-4 py-3 ${txt}`}>{isAr ? 'العنوان' : 'Title'}</th>
                  <th className={`px-4 py-3 ${txt}`}>{t.status}</th>
                  <th className={`px-4 py-3 ${txt}`}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className={`px-4 py-3 font-mono text-xs text-slate-500 ${txt}`}>{it.order}</td>
                    <td className="px-4 py-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        {it.image ? (
                          <img src={absoluteMediaUrl(it.image)} alt="" className="w-full h-full object-cover" />
                        ) : it.pdf ? (
                          <FileText className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ImageOff className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-3 ${txt}`}>
                      <div className="font-medium text-slate-900 dark:text-white">{isAr ? (it.title_ar || it.title_en) : (it.title_en || it.title_ar)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        it.published
                          ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      }`}>
                        {it.published ? t.published : (isAr ? 'مخفي' : 'Hidden')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                        <button type="button" onClick={() => openEditor(it)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-brand-blue/10 hover:text-brand-blue-700 text-xs font-semibold">
                          <Edit3 className="w-3.5 h-3.5" /> {t.edit}
                        </button>
                        <button type="button" onClick={() => remove(it.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-700 text-xs font-semibold">
                          <Trash2 className="w-3.5 h-3.5" /> {t.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Drawer editor ===== */}
      {editing && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className={`w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-6 ${txt}`}>
            <div className={`flex items-center justify-between mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{editing.id ? t.edit : (isAr ? 'إضافة جديد' : 'Add new')}</h3>
              <button type="button" onClick={() => setEditing(null)} className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            {error && <div className="mb-3 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
            <div className="space-y-4">

            <div className="grid md:grid-cols-2 gap-3">
              <Field label={t.titleAr} dir="rtl">
                <input
                  className={inputCls}
                  dir="rtl"
                  value={editing.title_ar}
                  onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })}
                />
              </Field>

              <Field label={t.titleEn} dir="ltr">
                <input
                  className={inputCls}
                  dir="ltr"
                  value={editing.title_en}
                  onChange={(e) => setEditing({ ...editing, title_en: e.target.value })}
                />
              </Field>
            </div>
            {collection !== 'gallery' &&
            collection !== 'certificates' &&
            collection !== 'client-logos' &&
            collection !== 'partners' && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label={isAr ? 'الوصف (عربي)' : 'Description (Arabic)'} dir="rtl">
                <textarea
                  rows={4}
                  className={inputCls}
                  dir="rtl"
                  value={editing.description_ar}
                  onChange={(e) =>
                    setEditing({ ...editing, description_ar: e.target.value })
                  }
                />
              </Field>

              <Field label={isAr ? 'الوصف (إنجليزي)' : 'Description (English)'} dir="ltr">
                <textarea
                  rows={4}
                  className={inputCls}
                  dir="ltr"
                  value={editing.description_en}
                  onChange={(e) =>
                    setEditing({ ...editing, description_en: e.target.value })
                  }
                />
              </Field>
            </div>
          )}
              {collection !== 'certificates' && (
              <Field label={isAr ? 'الصورة / الشعار' : 'Image / logo'}>
                {editing.image ? (
                  <div className="flex items-center gap-3">
                    <img src={absoluteMediaUrl(editing.image)} alt="" className="w-24 h-24 rounded-lg object-cover border" />
                    <button type="button" onClick={() => setEditing({ ...editing, image: '' })} className="text-xs text-red-600">{isAr ? 'إزالة' : 'Remove'}</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setPicker({ field: 'image', accept: 'image/*' })} className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 hover:border-brand-blue">{t.uploadOrPaste}</button>
                )}
              </Field>
              )}


              {showPdf && (
                <Field label={isAr ? 'ملف الشهادة (PDF)' : 'Certificate PDF'}>
                  {editing.pdf ? (
                    <div className="flex items-center gap-3">
                      <a href={absoluteMediaUrl(editing.pdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">
                        <FileText className="w-4 h-4" /> {isAr ? 'عرض PDF' : 'View PDF'}
                      </a>
                      <button type="button" onClick={() => setEditing({ ...editing, pdf: '' })} className="text-xs text-red-600">{isAr ? 'إزالة' : 'Remove'}</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setPicker({ field: 'pdf', accept: 'application/pdf' })} className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 hover:border-brand-blue">{isAr ? 'رفع PDF' : 'Upload PDF'}</button>
                  )}
                </Field>
              )}

              {categoryOptions && (
                <Field label={isAr ? 'الفئة' : 'Category'}>
                  <select
                    data-testid={`admin-${collection}-category`}
                    className={inputCls}
                    value={editing.category || ''}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  >
                    <option value="">{isAr ? '— بدون فئة —' : '— No category —'}</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {isAr ? (opt.ar || opt.value) : (opt.en || opt.value)}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              {projectFields && (
                <ProjectMetadataFields
                  isAr={isAr}
                  metadata={editing.metadata || {}}
                  onChange={(next) => setEditing({ ...editing, metadata: { ...(editing.metadata || {}), ...next } })}
                />
              )}

              {showMetadata && !projectFields && (
                <Field label={isAr ? 'حقول إضافية (JSON)' : 'Additional fields (JSON)'}>
                  <textarea
                    rows={6}
                    dir="ltr"
                    className={`${inputCls} font-mono text-xs`}
                    value={metadataText}
                    onChange={(e) => setMetadataText(e.target.value)}
                    placeholder='{ "lat": 21.5, "lng": 39.2, "client_ar": "...", "client_en": "..." }'
                  />
                  {metadataErr && (
                    <div className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                      {isAr ? 'خطأ في JSON: ' : 'JSON error: '}{metadataErr}
                    </div>
                  )}
                  <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {isAr
                      ? 'اترك القيمة {} إن لم تكن بحاجة لحقول إضافية.'
                      : 'Leave as {} if no additional fields are needed.'}
                  </div>
                </Field>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <Field label={isAr ? 'ترتيب العرض' : 'Display order'}>
                  <input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })} />
                </Field>
                <Field label={t.status}>
                  <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-pointer ${isAr ? 'flex-row-reverse' : ''}`}>
                    <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
                    <span className="text-sm">{editing.published ? t.published : (isAr ? 'مخفي' : 'Hidden')}</span>
                  </label>
                </Field>
              </div>
            </div>

            <div className={`mt-6 flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
              <button type="button" onClick={save} disabled={saving} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t.save}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold">{isAr ? 'إلغاء' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}

      <MediaPicker
        open={!!picker}
        accept={picker?.accept || 'image/*'}
        onClose={() => setPicker(null)}
        onSelect={(url) => { if (picker) setEditing((p) => ({ ...p, [picker.field]: url })); }}
      />
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children }) => (
  <div>
    <label dir={dir} className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{label}</label>
    {children}
  </div>
);

/* ============================================================
   ProjectMetadataFields — dedicated UI for the projects collection.
   Replaces the raw JSON textarea with explicit inputs for lat/lng,
   bilingual client/region, and an interactive map (click to set coords).
   Values are still written to the `metadata` object with the same keys
   the public frontend already reads (lat, lng, client_ar/en, region_ar/en).
   ============================================================ */
const pinIcon = L.divIcon({
  className: 'ebc-admin-marker',
  html: `<div style="width:22px;height:22px;border-radius:9999px;
                    background:linear-gradient(135deg,#fbbf24,#f58720);
                    border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const ClickToSetCoords = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

const FlyToCoords = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      map.flyTo([lat, lng], Math.max(map.getZoom(), 7), { duration: 0.8 });
    }
  }, [lat, lng, map]);
  return null;
};

const ProjectMetadataFields = ({ isAr, metadata, onChange }) => {
  const lat = Number(metadata.lat);
  const lng = Number(metadata.lng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const initialCenter = hasCoords ? [lat, lng] : [24.7136, 46.6753]; // Riyadh default

  const setCoords = (nextLat, nextLng) => {
    onChange({ lat: Number(nextLat.toFixed(6)), lng: Number(nextLng.toFixed(6)) });
  };

  return (
    <div className="space-y-3 pt-1">
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
        {isAr ? 'موقع المشروع على الخريطة' : 'Project location'}
      </div>
      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={{ height: 260 }}>
        <MapContainer
          center={initialCenter}
          zoom={hasCoords ? 7 : 5}
          scrollWheelZoom
          style={{ width: '100%', height: '100%' }}
          data-testid="admin-project-map"
        >
          <TileLayer
            url={`https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${MAPTILER_KEY}&language=${isAr ? 'ar' : 'en'}`}
            attribution='&copy; MapTiler &copy; OpenStreetMap'
          />
          <ClickToSetCoords onPick={setCoords} />
          {hasCoords && <Marker position={[lat, lng]} icon={pinIcon} />}
          {hasCoords && <FlyToCoords lat={lat} lng={lng} />}
        </MapContainer>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {isAr
          ? 'اضغط على الخريطة لتحديد موقع المشروع تلقائياً.'
          : 'Click anywhere on the map to set the project coordinates.'}
      </p>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label={isAr ? 'خط العرض (Latitude)' : 'Latitude'}>
          <input
            type="number" step="any" dir="ltr"
            data-testid="admin-project-lat"
            className={inputCls}
            value={metadata.lat ?? ''}
            onChange={(e) => onChange({ lat: e.target.value === '' ? '' : Number(e.target.value) })}
            placeholder="21.4858"
          />
        </Field>
        <Field label={isAr ? 'خط الطول (Longitude)' : 'Longitude'}>
          <input
            type="number" step="any" dir="ltr"
            data-testid="admin-project-lng"
            className={inputCls}
            value={metadata.lng ?? ''}
            onChange={(e) => onChange({ lng: e.target.value === '' ? '' : Number(e.target.value) })}
            placeholder="39.1925"
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label={isAr ? 'العميل (عربي)' : 'Client (Arabic)'} dir="rtl">
          <input
            dir="rtl"
            data-testid="admin-project-client-ar"
            className={inputCls}
            value={metadata.client_ar || ''}
            onChange={(e) => onChange({ client_ar: e.target.value })}
          />
        </Field>
        <Field label={isAr ? 'العميل (إنجليزي)' : 'Client (English)'} dir="ltr">
          <input
            dir="ltr"
            data-testid="admin-project-client-en"
            className={inputCls}
            value={metadata.client_en || ''}
            onChange={(e) => onChange({ client_en: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label={isAr ? 'المنطقة (عربي)' : 'Region (Arabic)'} dir="rtl">
          <input
            dir="rtl"
            data-testid="admin-project-region-ar"
            className={inputCls}
            value={metadata.region_ar || ''}
            onChange={(e) => onChange({ region_ar: e.target.value })}
            placeholder={isAr ? 'مثال: منطقة مكة' : ''}
          />
        </Field>
        <Field label={isAr ? 'المنطقة (إنجليزي)' : 'Region (English)'} dir="ltr">
          <input
            dir="ltr"
            data-testid="admin-project-region-en"
            className={inputCls}
            value={metadata.region_en || ''}
            onChange={(e) => onChange({ region_en: e.target.value })}
            placeholder="e.g. Makkah Region"
          />
        </Field>
      </div>
    </div>
  );
};

export default AdminContent;
